import { PortalEditorImageInteractionConfig } from '@src/ui/Editor/constants/editorImageInteractionConfig';
import { PortalEditorObjectPosition } from '@src/ui/Editor/types';
import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';

interface UseTiptapImageInteractionsProps {
  editor: Editor | null;
  enableImageAltEdit?: boolean;
}

export function useTiptapImageInteractions({
  editor,
  enableImageAltEdit = false,
}: UseTiptapImageInteractionsProps) {
  const [isImageAltPopupOpen, setIsImageAltPopupOpen] = useState(false);
  const [imageAltPopupPosition, setImageAltPopupPosition] = useState<PortalEditorObjectPosition>({
    top: 0,
    left: 0,
  });
  const [currentImageAlt, setCurrentImageAlt] = useState('');
  const [selectedImagePos, setSelectedImagePos] = useState<number | null>(null);
  const [selectedImageElement, setSelectedImageElement] = useState<HTMLElement | null>(null);

  const updateImageAlt = useCallback(
    (altText: string) => {
      if (!editor || selectedImagePos === null) {
        return;
      }

      const tr = editor.state.tr;
      const node = editor.state.doc.nodeAt(selectedImagePos);

      if (node && node.type.name === 'image') {
        tr.setNodeMarkup(selectedImagePos, undefined, {
          ...node.attrs,
          alt: altText,
        });
        editor.view.dispatch(tr);
        setCurrentImageAlt(altText);
      }
    },
    [editor, selectedImagePos],
  );

  const handleClosePopup = useCallback(() => {
    setIsImageAltPopupOpen(false);
    setSelectedImagePos(null);
    setSelectedImageElement(null);
  }, []);

  // 画像の削除検知
  useEffect(() => {
    if (!editor || !enableImageAltEdit || !isImageAltPopupOpen || selectedImagePos === null) {
      return;
    }

    const handleUpdate = () => {
      const node = editor.state.doc.nodeAt(selectedImagePos);
      if (!node || node.type.name !== 'image') {
        handleClosePopup();
      }
    };

    editor.on('update', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor, enableImageAltEdit, isImageAltPopupOpen, selectedImagePos, handleClosePopup]);

  useEffect(() => {
    if (!editor || !enableImageAltEdit) {
      return;
    }

    const handleImageClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName !== 'IMG') {
        return;
      }

      const proseMirrorContainer = target.closest('.ProseMirror');
      if (!proseMirrorContainer) {
        return;
      }

      const pos = editor.view.posAtDOM(target, 0);
      const node = editor.state.doc.nodeAt(pos);

      if (node && node.type.name === 'image') {
        const imageWrapper =
          target.closest('.resizable-image-wrapper') || target.parentElement?.parentElement;

        if (imageWrapper) {
          const rect = imageWrapper.getBoundingClientRect();

          setImageAltPopupPosition({
            top: rect.top - PortalEditorImageInteractionConfig.popupHeight,
            left: rect.left,
          });

          setCurrentImageAlt(node.attrs.alt || '');
          setSelectedImagePos(pos);
          setSelectedImageElement(imageWrapper as HTMLElement);
          setIsImageAltPopupOpen(true);

          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    const editorDom = editor.view.dom;
    editorDom.addEventListener('click', handleImageClick, true);

    return () => {
      editorDom.removeEventListener('click', handleImageClick, true);
    };
  }, [editor, enableImageAltEdit]);

  // 画像の外側クリックの制御
  useEffect(() => {
    if (!editor || !enableImageAltEdit || !isImageAltPopupOpen) {
      return;
    }

    const handleEditorClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'IMG' && target.closest('.ProseMirror')) {
        return;
      }

      handleClosePopup();
    };

    const editorDom = editor.view.dom;

    const timeoutId = setTimeout(() => {
      editorDom.addEventListener('click', handleEditorClick, true);
    }, PortalEditorImageInteractionConfig.actionTimeout);

    return () => {
      clearTimeout(timeoutId);
      editorDom.removeEventListener('click', handleEditorClick, true);
    };
  }, [editor, enableImageAltEdit, isImageAltPopupOpen, handleClosePopup]);

  useEffect(() => {
    if (!isImageAltPopupOpen || !selectedImageElement) {
      return;
    }

    const updatePopupPosition = () => {
      const rect = selectedImageElement.getBoundingClientRect();
      setImageAltPopupPosition({
        top: rect.top - PortalEditorImageInteractionConfig.popupHeight,
        left: rect.left,
      });
    };

    const handleScroll = () => {
      updatePopupPosition();
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isImageAltPopupOpen, selectedImageElement]);

  return {
    isImageAltPopupOpen,
    imageAltPopupPosition,
    currentImageAlt,
    updateImageAlt,
    handleClosePopup,
  };
}
